import { LitElement, html, render } from 'lit';
import { property, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { GridElement } from '@vaadin/vaadin-grid/vaadin-grid';
import { GridColumnElement } from '@vaadin/vaadin-grid/vaadin-grid-column';
import { isElementOverflowed, OverflowDirection } from '../../../utils/htmlElement.utils';
import { GzTooltip } from '../../../ui/gzTooltip';
import { BaseColumnDetails } from '../../types/common';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { isEqual } from 'lodash';
import { DEFAULT_COLUMN_MIN_WIDTH } from '../../gridView/types';

export class LightGridWebComponent<TGridItem = unknown> extends LitElement {
  @property({ type: Array, attribute: true, reflect: true })
  items: TGridItem[];

  /**
   * This object will contain the custom renderer functions for the columns. If a renderer function is found for a
   * column key, it will be used to display the column data instead of the default renderer
   */
  @property({ type: Object, attribute: true, reflect: false })
  columnRenderers: Record<string, Function> = {};

  /**
   * The array of grid columns. In the BaseColumnDetails interface only the key and name properties can be set.
   */
  @property({ type: Array, attribute: true, reflect: true })
  columns: BaseColumnDetails[];

  @property({ type: Boolean, attribute: true, reflect: true })
  rowBorders = false;

  @query('#lightGrid')
  lightGrid: GridElement;

  static get styles() {
    return styles;
  }

  protected shouldUpdate(changedProperties): boolean {
    return !isEqual(changedProperties.get('items'), this.items);
  }

  render() {
    return html`
      <vaadin-grid
        id="lightGrid"
        part="light-grid"
        .items="${this.items}"
        height-by-rows
        theme="compact no-border ${this.rowBorders ? "" : "no-row-borders"}"
        @column-resize="${this.updateLightGridColumnSizes}"
      >
        ${this.renderGridColumns()}
      </vaadin-grid>
    `;
  }

  private renderGridColumns(): GridColumnElement[] | undefined {
    if (isEmpty(this.columns)) {
      return;
    }
    return this.columns.map(column => this.getGridColumn(column));
  }

  private getGridColumn(column: BaseColumnDetails): GridColumnElement {
    const columnElement = document.createElement('vaadin-grid-column') as GridColumnElement;

    columnElement.resizable = true;
    columnElement.path = column.key;
    columnElement.header = column.name;
    columnElement.flexGrow = column.flexGrow ? column.flexGrow : 1;

    if (column.width) {
      columnElement.width = column.width;
      columnElement.flexGrow = 0;
    }

    if (column.frozen) {
      columnElement.frozen = true;
      columnElement.resizable = false;
    }

    columnElement.headerRenderer = root => {
      render(
        html`
          <span @mouseenter="${this.handleMouseEnter}" @mousemove="${GzTooltip.align}" @mouseleave="${GzTooltip.hide}">
            <span class="text-semibold cell-label cell-label--header">${column.name}</span>
          </span>
        `,
        root
      );
    };

    columnElement.renderer = (root, _, rowData) => {
      return this.columnCellRenderer(root, rowData, column);
    };

    return columnElement;
  }

  private handleMouseEnter(event: MouseEvent) {
    const item = event.target as Element;
    const label = item.querySelector('.cell-label') as HTMLElement;

    if (!label || !isElementOverflowed(label, OverflowDirection.HORIZONTAL)) {
      return;
    }

    const tooltipText = label.textContent || '-';
    GzTooltip.show(tooltipText, event);
  }

  private columnCellRenderer(root, rowData, columnDetails) {
    let content = rowData.item[columnDetails.key];

    if (this.columnRenderers && this.columnRenderers[columnDetails.key]) {
      content = this.columnRenderers[columnDetails.key](root, columnDetails, rowData);
    }

    render(
      html`
        <span @mouseenter="${this.handleMouseEnter}" @mousemove="${GzTooltip.align}" @mouseleave="${GzTooltip.hide}">
          <span class="cell-label">${content}</span>
        </span>
      `,
      root
    );
  }

  private updateLightGridColumnSizes(event: CustomEvent): void {
    const resizedColumn = event.detail.resizedColumn;
    if (!resizedColumn) {
      throw new Error('Unable to retrieve column!');
    }
    const pixelsRegExp = new RegExp('[0-9]+px');

    if (!resizedColumn.width || resizedColumn.width.search(pixelsRegExp) === -1) {
      throw new Error('Unable to parse column width!');
    }

    // skip non-resizable columns
    if (!resizedColumn.resizable) {
      return;
    }

    const columnSize = parseInt(resizedColumn.width, 10);
    if (columnSize < DEFAULT_COLUMN_MIN_WIDTH) {
      resizedColumn.width = DEFAULT_COLUMN_MIN_WIDTH + 'px';
    }
  }
}
